<template>
  <LayoutEntity :navigation-items="[]">
    <template #subheading></template>
    <template #headingNew>New {{ entityLabel }} Change Notification</template>
    <template #default>
      <FormKit
        type="form"
        id="theFormId"
        :value="changeNotification"
        v-model="changeNotification"
        @submit="handleSubmit"
        :actions="false"
        autocomplete="off"
      >
        <template #default="{ state: formState }">
          <div class="pb-5 sm:flex-1 sm:overflow-auto">
            <FormSection
              v-if="
                (distributorId && Object.keys(changeNotification).length > 0) || (!distributorId && changeNotification)
              "
            >
              <template #sectionName>Change Notification Details</template>
              <template #sectionDescription>
                Please notify the Deposit Beverage Container Program if there are any changes to the following:
                <ul class="ml-4 list-disc">
                  <li>Company ownership</li>
                  <li>Business name</li>
                  <li>Mailing address</li>
                  <li>Operational status</li>
                </ul>
              </template>
              <template #default>
                <DistributorAutocomplete
                  label="Distributor Name"
                  :entity="changeNotification"
                  v-model="assignedToRef"
                ></DistributorAutocomplete>
                <FormKit
                  label="Notification Reason"
                  name="notificationReason"
                  type="textarea"
                  validation="required:trim|length:0,255"
                />
              </template>
            </FormSection>
            <div class="mx-0 mt-6 sm:mx-5 lg:mx-10" v-else>
              <div class="lg:grid lg:grid-cols-3">
                <div class="mb-2 lg:col-span-2 lg:mt-0">
                  <SkeletonBlock class="h-10 w-40 overflow-hidden rounded-md" />
                </div>
                <div class="lg:col-span-2 lg:mt-0">
                  <SkeletonBlock class="h-40 w-full overflow-hidden rounded-md p-4" />
                </div>
              </div>
            </div>
          </div>
          <FormActions
            :save-action="saveAction"
            :cancel-action="cancelAction"
            :form-state="formState"
            save-label="Submit"
          ></FormActions
        ></template>
      </FormKit>
    </template>
  </LayoutEntity>
</template>

<script lang="ts">
import { routeNames } from "@/routes";
import {
  type ChangeNotification,
  type ChangeNotificationFormKit,
  type ChangeNotificationResponse,
} from "@/views/changeNotification/changeNotification";
import { createChangeNotification } from "@/views/changeNotification/changeNotificationApi";
import { FormKit, submitForm } from "@formkit/vue";
import { FormActions, FormSection } from "@modernary/mui/components/form";
import { LayoutEntity } from "@modernary/mui/components/layouts";
import { useApi } from "@modernary/mui/utils/api";
import { routeCancelAction } from "@modernary/mui/utils/helpers";
import { defineComponent, ref, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getDistributorById } from "../distributor/distributorApi";
import DistributorAutocomplete from "@/views/changeNotification/DistributorAutocomplete.vue";
import { SkeletonBlock } from "@modernary/mui/components/common";

export default defineComponent({
  name: "ChangeNotification",
  components: {
    LayoutEntity,
    FormKit,
    FormSection,
    FormActions,
    DistributorAutocomplete,
    SkeletonBlock,
  },
  props: {
    entityLabel: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();

    const route = useRoute();
    const distributorId = route.params.distributorId;

    const changeNotification = ref<ChangeNotification>();

    if (distributorId) {
      const { exec: fetchDistributor } = useApi(getDistributorById, {
        responseAdapter: async (response) => {
          return response.data;
        },
      });
      fetchDistributor(distributorId).then(async (result) => {
        changeNotification.value = {
          distributorId: route.params.distributorId.toString(),
          distributorName: result?.name,
        };
      });
    } else {
      changeNotification.value = {};
    }

    const { exec: execCreateChangeNotification, isStatusPending } = useApi<
      ChangeNotification,
      ChangeNotificationResponse
    >(createChangeNotification, {
      responseAdapter: async (response) => {
        // Things get awkward when our API returns a very different type than we're creating.
        const result = response.data as unknown as ChangeNotificationResponse;
        if (result.wasRequestSubmitted) {
          cancelAction();
        }

        return result;
      },
    });

    const assignedToRef = ref<{ distributorName?: string }>({});

    const handleSubmit = async (data: ChangeNotification) => {
      data.distributorName = assignedToRef.value?.distributorName;
      return execCreateChangeNotification(data);
    };

    const saveAction = function () {
      return submitForm("theFormId");
    };

    const cancelAction = function () {
      routeCancelAction(router, routeNames.home);
    };

    return {
      handleSubmit,
      saveAction,
      cancelAction,
      assignedToRef,
      changeNotification: changeNotification as Ref<ChangeNotificationFormKit>,
      distributorId,
    };
  },
});
</script>
